import { useQuery } from "@apollo/client";
import CloseIcon from "@mui/icons-material/Close";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import {
  Box,
  IconButton,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { SpecialZoomLevel } from "@react-pdf-viewer/core";
import { Dialog } from "components/Dialog";
import { FilePreview } from "components/Document/shared/FilePreview";
import { gql } from "generated-graphql";
import { useCurrentUser } from "hooks/useCurrentUser";
import { useDocumentDownloadLink } from "hooks/useDocumentDownloadLink";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";

import { computeCompareFooterCounts, parseCompareParam } from "./CompareSourcesDialog.helpers";
import {
  CompareSourcesTable,
  CompareSourcesTableRow,
  toCompareSourcesTableRows,
} from "./CompareSourcesTable";

export const COMPARE_MANIFEST_SOURCES_QUERY = gql(`
  query CompareSources($processedManifestId: ID!, $wasteLineUnifiedIds: [ID!]) {
    compareManifestSources(
      processedManifestId: $processedManifestId
      wasteLineUnifiedIds: $wasteLineUnifiedIds
    ) {
      availableSources
      differenceCount
      missingCount
      rows {
        key
        group
        field
        label
        eManifest
        vendor
        document
        differs
        missingInSomeSource
        availableSources
      }
    }
  }
`);

export const COMPARE_MANIFEST_PDF_QUERY = gql(`
  query CompareManifestPdf($search: String!) {
    processedManifests(search: $search, page: 0, pageSize: 1) {
      items {
        id
        manifestTrackingNumber
        manifestPdf {
          id
          fileExtension
        }
      }
    }
  }
`);

/** Height of the table's sticky header row; the PDF rail header matches it. */
const STICKY_HEADER_HEIGHT = "2.5rem";

interface CompareSourcesDialogProps {
  open: boolean;
  onClose: () => void;
  processedManifestId: string;
  /** When provided, scopes the comparison to these line items (omits the Manifest group). */
  wasteLineUnifiedIds?: string[];
  /** When set, the matching field row is highlighted + scrolled into view on open. */
  highlightField?: string;
}

export function CompareSourcesDialog({
  open,
  onClose,
  processedManifestId,
  wasteLineUnifiedIds,
  highlightField,
}: CompareSourcesDialogProps) {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const fullScreen = useMediaQuery(theme.breakpoints.down("md"));
  const { tenantId } = useParams<{ tenantId: string }>();

  // The grey header background and divider color are shared with the PDF rail's
  // header so the two surfaces stay visually aligned. The data table owns its
  // own copies of these (and the divergence/highlight accents).
  const headerBg = isDark ? theme.palette.grey[900] : theme.palette.grey[100];
  const gridBorder = theme.palette.divider;

  const [showPdf, setShowPdf] = useState(false);
  const { data, loading } = useQuery(COMPARE_MANIFEST_SOURCES_QUERY, {
    variables: {
      processedManifestId,
      wasteLineUnifiedIds: wasteLineUnifiedIds ?? null,
    },
    skip: !open,
  });

  const { data: pdfData } = useQuery(COMPARE_MANIFEST_PDF_QUERY, {
    variables: { search: `tenantId:${tenantId} id:${processedManifestId}` },
    skip: !open || !tenantId,
  });

  const manifestSummary = pdfData?.processedManifests.items[0] ?? null;
  const manifestPdf = manifestSummary?.manifestPdf ?? null;
  const manifestTrackingNumber = manifestSummary?.manifestTrackingNumber ?? null;
  const showPreviewRail = showPdf && manifestPdf != null;

  // Resolve the manifest PDF's signed URL with the same hook the Documents UI
  // uses, then hand it to the shared <FilePreview> viewer (rather than fetching
  // + rendering a bespoke previewer here).
  const [getDownloadLink] = useDocumentDownloadLink();
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  useEffect(() => {
    const docId = manifestPdf?.id;
    if (!showPdf || !docId) {
      setPreviewUrl(null);
      return;
    }
    let cancelled = false;
    getDownloadLink({ variables: { id: docId } })
      .then((result) => {
        if (!cancelled) setPreviewUrl(result.data?.getDocumentDownloadLink ?? null);
      })
      .catch(() => {
        if (!cancelled) setPreviewUrl(null);
      });
    return () => {
      cancelled = true;
    };
  }, [showPdf, manifestPdf?.id, getDownloadLink]);

  // Map the GraphQL rows (whose nullable string fields are optional) to the
  // table's presentational row shape (which uses `string | null`).
  const allRows = useMemo<CompareSourcesTableRow[]>(
    () => toCompareSourcesTableRows(data?.compareManifestSources.rows ?? []),
    [data?.compareManifestSources.rows]
  );

  const availableSources = data?.compareManifestSources.availableSources ?? [];

  // Audit summary counts: prefer the server totals (over the full row set),
  // falling back to a client recompute if the query hasn't resolved yet.
  const summaryCounts = useMemo(() => {
    if (data?.compareManifestSources) {
      return {
        differenceCount: data.compareManifestSources.differenceCount,
        missingCount: data.compareManifestSources.missingCount,
      };
    }
    return computeCompareFooterCounts(allRows);
  }, [data?.compareManifestSources, allRows]);

  const openPdfInNewTab = () => {
    if (previewUrl) window.open(previewUrl, "_blank");
  };

  const renderPreviewBody = () => {
    if (!previewUrl || !manifestPdf) {
      return <Skeleton variant="rectangular" animation="wave" height="100%" width="100%" />;
    }
    return (
      <FilePreview
        fileUrl={previewUrl}
        fileExtension={manifestPdf.fileExtension ?? "pdf"}
        defaultScale={SpecialZoomLevel.PageFit}
      />
    );
  };

  const titleBarActions = (
    <Stack direction="row" alignItems="center" spacing={1}>
      {manifestPdf && (
        <Tooltip title={showPdf ? "Hide manifest PDF" : "Show manifest PDF"}>
          <IconButton
            size="small"
            onClick={() => setShowPdf((prev) => !prev)}
            color={showPdf ? "primary" : "default"}
          >
            <DescriptionOutlinedIcon sx={{ fontSize: 20 }} />
          </IconButton>
        </Tooltip>
      )}
    </Stack>
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xl"
      fullWidth
      fullScreen={fullScreen}
      noPrimaryAction
      title={
        <Box sx={{ display: "flex", alignItems: "baseline", gap: 1.5, flexWrap: "wrap" }}>
          <span>
            {wasteLineUnifiedIds
              ? "Compare Manifest Data Sources — Line Items"
              : "Compare Manifest Data Sources"}
          </span>
          {manifestTrackingNumber && (
            <Box
              component="span"
              sx={{
                fontFamily: "monospace",
                fontSize: "0.8125rem",
                fontWeight: 400,
                color: "text.secondary",
                letterSpacing: "0.02em",
              }}
            >
              {manifestTrackingNumber}
            </Box>
          )}
        </Box>
      }
      titleBarActionElement={titleBarActions}
      contentSx={{ p: 0, overflow: "hidden" }}
      slotProps={{
        paper: { sx: { height: fullScreen ? "100%" : "90vh", maxHeight: "90vh" } },
      }}
    >
      <Box sx={{ flex: 1, display: "flex", flexDirection: "column", minHeight: 0 }}>
        <Box
          sx={{ flex: 1, display: "flex", flexDirection: "row", minHeight: 0, overflow: "hidden" }}
        >
          <Box sx={{ flex: 3, minWidth: 0, overflow: "auto" }}>
            <CompareSourcesTable
              rows={allRows}
              availableSources={availableSources}
              differenceCount={summaryCounts.differenceCount}
              missingCount={summaryCounts.missingCount}
              loading={loading}
              hasManifestGroup
              highlightField={highlightField}
              compact={showPreviewRail}
            />
          </Box>

          {showPreviewRail && (
            <>
              <Box
                sx={{
                  // 40% to the PDF viewer (60% to the data-source table).
                  flex: 2,
                  minWidth: 0,
                  // minHeight:0 lets this column shrink to the available height
                  // so the PDF viewer fits instead of pushing past the modal.
                  minHeight: 0,
                  // Bottom inset matching the viewer's right margin so the PDF
                  // doesn't sit flush against the dialog's bottom edge.
                  pb: 3,
                  display: "flex",
                  flexDirection: "column",
                  overflow: "hidden",
                }}
              >
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  sx={{
                    px: 2,
                    height: STICKY_HEADER_HEIGHT,
                    flexShrink: 0,
                    borderTop: `1px solid ${gridBorder}`,
                    borderLeft: `1px solid ${gridBorder}`,
                    borderRight: `1px solid ${gridBorder}`,
                    // No bottom border: the PDF viewer below has its own full
                    // outline, so its top edge is the single separator (avoids a
                    // doubled 2px line under this header).
                    bgcolor: headerBg,
                  }}
                >
                  <Typography variant="subtitle2">Manifest PDF</Typography>
                  <Stack direction="row" spacing={0.5}>
                    {previewUrl && (
                      <Tooltip title="Open in new tab">
                        <IconButton size="small" onClick={openPdfInNewTab}>
                          <OpenInNewIcon sx={{ fontSize: 18 }} />
                        </IconButton>
                      </Tooltip>
                    )}
                    <Tooltip title="Hide PDF">
                      <IconButton size="small" onClick={() => setShowPdf(false)}>
                        <CloseIcon sx={{ fontSize: 18 }} />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                </Stack>
                {/* Pin the viewer to this box so the PDF's intrinsic height can't
                    stretch the rail past the modal. */}
                <Box sx={{ flex: 1, minHeight: 0, position: "relative" }}>
                  <Box sx={{ position: "absolute", inset: 0, overflow: "hidden" }}>
                    {renderPreviewBody()}
                  </Box>
                </Box>
              </Box>
            </>
          )}
        </Box>
      </Box>
    </Dialog>
  );
}

/** The `?compare=` URL param key this permalink surface reads/writes. */
export const COMPARE_PARAM_KEY = "compare";

interface CompareSourcesPermalinkProps {
  /**
   * The manifest the `?compare=` permalink resolves against. The permalink only
   * opens when this matches the route's current manifest, so the host renders
   * it scoped to the manifest in view.
   */
  processedManifestId: string;
}

/**
 * URL-driven open surface for {@link CompareSourcesDialog}. Reads the
 * `?compare=` search param and opens the dialog accordingly:
 *
 * - `compare=open` — full-manifest comparison.
 * - `compare=line:<wluId>` — scoped to a single line item.
 * - `compare=field:<fieldName>` — full-manifest comparison with that field row
 *   highlighted and scrolled into view.
 *
 * Closing the dialog clears the param. Mount this once on the manifest route
 * (call sites that open the dialog directly should render
 * {@link CompareSourcesDialog} themselves).
 */
export function CompareSourcesPermalink({ processedManifestId }: CompareSourcesPermalinkProps) {
  const [searchParams, setSearchParams] = useSearchParams();
  const { isStaff } = useCurrentUser();
  // Staff-only, matching the in-drawer compare entry points — keeps the
  // `?compare=` deep-link inert for non-staff rather than gating only the button.
  const compareEnabled = isStaff;
  const parsed = useMemo(
    () => parseCompareParam(searchParams.get(COMPARE_PARAM_KEY)),
    [searchParams]
  );

  const handleClose = useCallback(() => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete(COMPARE_PARAM_KEY);
        return next;
      },
      { replace: true }
    );
  }, [setSearchParams]);

  if (!parsed || !compareEnabled) return null;

  const wasteLineUnifiedIds = parsed.mode === "line" ? [parsed.wasteLineUnifiedId] : undefined;
  const highlightField = parsed.mode === "field" ? parsed.field : undefined;

  return (
    <CompareSourcesDialog
      open
      onClose={handleClose}
      processedManifestId={processedManifestId}
      wasteLineUnifiedIds={wasteLineUnifiedIds}
      highlightField={highlightField}
    />
  );
}
